// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — turning a seed into the letter stream, deterministically. This is the
// WHOLE P2P-sync surface: every peer feeds the frozen lobby seed through buildStream()
// and gets the BYTE-IDENTICAL sequence of letters, so no letter ever crosses the wire.
//
// The stream is a BAG with a guaranteed vowel ratio (so no all-consonant board can be
// dealt) then a seeded shuffle. Letters within the vowel and consonant pools are drawn
// by rough English frequency, so common letters dominate but a rare Q/Z is possible.

import { makeRng, randInt, shuffle, hashSeed, type Rng } from '@ben-gy/game-engine/rng';
import { cellCount, type Mode } from './modes';

/** Rough English letter frequencies (per-mille), split into vowels / consonants. */
const VOWEL_WEIGHTS: Array<[string, number]> = [
  ['E', 127], ['A', 82], ['O', 75], ['I', 70], ['U', 28],
];
const CONSONANT_WEIGHTS: Array<[string, number]> = [
  ['T', 91], ['N', 67], ['S', 63], ['R', 60], ['H', 61], ['L', 40], ['D', 43],
  ['C', 28], ['M', 24], ['W', 24], ['F', 22], ['G', 20], ['Y', 20], ['P', 19],
  ['B', 15], ['V', 10], ['K', 8], ['J', 2], ['X', 2], ['Q', 2], ['Z', 2],
];

function drawWeighted(rng: Rng, weights: ReadonlyArray<[string, number]>): string {
  const total = weights.reduce((s, [, w]) => s + w, 0);
  let roll = randInt(rng, 1, total);
  for (const [ch, w] of weights) {
    roll -= w;
    if (roll <= 0) return ch;
  }
  return weights[weights.length - 1][0];
}

/** The full deterministic letter stream for a mode + seed (length = cell count). */
export function buildStream(mode: Mode, seed: number): string[] {
  const rng = makeRng(seed >>> 0);
  const n = cellCount(mode);
  const vowels = Math.round(n * mode.vowelRatio);
  const bag: string[] = [];
  // Draw the guaranteed vowels first, then consonants, so the ratio is exact...
  for (let i = 0; i < vowels; i++) bag.push(drawWeighted(rng, VOWEL_WEIGHTS));
  for (let i = vowels; i < n; i++) bag.push(drawWeighted(rng, CONSONANT_WEIGHTS));
  // ...then shuffle so vowels are not all front-loaded.
  return shuffle(rng, bag);
}

/** Today's date in UTC as YYYY-MM-DD — the same string for every player on Earth. */
export function todayUTC(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/** The daily seed for a mode: identical worldwide, changes at UTC midnight. */
export function dailySeed(mode: Mode, dateStr: string): number {
  return hashSeed(`wordloom:${mode.id}:${dateStr}`);
}
